import React, { useState, useEffect, useMemo } from 'react';
import { 
  MdBadge, 
  MdCheckCircle, 
  MdPerson,
  MdAdd,
  MdPeople,
  MdSettings,
  MdTrendingUp,
  MdTrendingDown,
  MdAccessTime,
  MdFlashOn
} from 'react-icons/md';
import { FaUserCircle } from 'react-icons/fa';
import { getDigitalCards, getUsers } from '../utils/dataManager';
import { getCurrentUser } from '../utils/auth';
import './Dashboard.css';

const Dashboard = () => {
  const [digitalCards, setDigitalCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  const currentUser = getCurrentUser();
  useEffect(() => {
    if (!currentUser) {
      window.location.href = '/';
      return;
    }
  }, [currentUser]);

  useEffect(() => {
    // Load real data
    const loadData = async () => {
      try {
        const cards = await getDigitalCards();
        const allUsers = await getUsers();
        
        setDigitalCards(cards);
        setUsers(allUsers);
        const activity = generateRecentActivity(cards, allUsers);
        setRecentActivity(activity);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setDigitalCards([]);
        setUsers([]);
        setRecentActivity([]);
      }
    };

    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Calculate real statistics and memoize to prevent unnecessary re-renders
  const currentStats = useMemo(() => {
    const publishedCards = digitalCards.filter(card => card.status === 'published');
    const userCards = digitalCards.filter(card => card.created_by === currentUser?.id);
    const activeUsers = users.filter(user => user.status === 'active');

    return {
      totalCards: digitalCards.length,
      publishedCards: publishedCards.length,
      activeUsers: activeUsers.length,
      myCards: userCards.length
    };
  }, [digitalCards, users, currentUser?.id]);
  useEffect(() => {
    const savePreviousStats = () => {
      try {
        localStorage.setItem('dashboard_previous_stats', JSON.stringify(currentStats));
      } catch (error) {
        console.error('Failed to save stats to localStorage');
      }
    };

    const timer = setTimeout(() => {
      savePreviousStats();
    }, 5000); // Save after 5 seconds
    
    return () => clearTimeout(timer);
  }, [currentStats]);

  // Early return after all hooks
  if (!currentUser) {
    return <div>Redirecting to login...</div>;
  }

  const generateRecentActivity = (cards, allUsers) => {
    const activities = [];
    
    // Add card creation activities
    cards.forEach(card => {
      const user = allUsers.find(u => u.id === card.created_by);
      activities.push({
        id: `card-${card.id}`,
        type: 'card_created',
        user: user?.username || 'Unknown User',
        action: `Created digital card for ${card.name}`,
        time: new Date(card.created_at || Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
        badge: 'success'
      });
    });
    allUsers.forEach(user => {
      if (user.created_at) {
        activities.push({
          id: `user-${user.id}`,
          type: 'user_registered',
          user: user.username,
          action: `Registered as ${user.role.replace('_', ' ')}`,
          time: new Date(user.created_at),
          badge: 'info'
        });
      }
    });
    return activities
      .sort((a, b) => b.time - a.time)
      .slice(0, 10);
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  // Get previous stats from localStorage
  const getPreviousStats = () => {
    try {
      const stored = localStorage.getItem('dashboard_previous_stats');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  };

  // Calculate real percentage change
  const calculateRealChange = (current, previous) => {
    if (current === 0) return null; 
    
    if (previous === null || previous === undefined) {
      if (current === 1) {
        return { text: 'New', positive: true };
      }
      return null;
    }

    if (previous === current) {
      return null;
    }

    if (previous === 0) {
      return { text: 'New', positive: true };
    }
    const changePercent = Math.round(((current - previous) / previous) * 100);
    const isPositive = changePercent > 0;
    
    return {
      text: `${isPositive ? '+' : ''}${changePercent}%`,
      positive: isPositive
    };
  };

  // Get previous stats and calculate changes
  const previousStats = getPreviousStats();

  const stats = [
    {
      title: 'Total Digital Cards',
      value: currentStats.totalCards,
      icon: <MdBadge />,
      color: 'cards',
      change: calculateRealChange(currentStats.totalCards, previousStats?.totalCards),
    },
    {
      title: 'Published Cards',
      value: currentStats.publishedCards,
      icon: <MdCheckCircle />,
      color: 'active',
      change: calculateRealChange(currentStats.publishedCards, previousStats?.publishedCards),
    },
    {
      title: 'Active Users',
      value: currentStats.activeUsers,
      icon: <MdPeople />,
      color: 'users',
      change: calculateRealChange(currentStats.activeUsers, previousStats?.activeUsers),
    },
    {
      title: 'My Cards',
      value: currentStats.myCards,
      icon: <MdPerson />,
      color: 'pending',
      change: calculateRealChange(currentStats.myCards, previousStats?.myCards),
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Welcome back, {currentUser?.username || 'User'}! Here's what's happening today.</p>
      </div>

      <div className="stats-table-container">
        <table className="stats-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Count</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((stat, index) => (
              <tr key={index} className="stat-row">
                <td className="stat-category">
                  <div className="category-cell">
                    <div className={`stat-icon ${stat.color}`}>
                      {stat.icon}
                    </div>
                    <span className="stat-title">{stat.title}</span>
                  </div>
                </td>
                <td className="stat-value">
                  <span className="stat-number">{stat.value}</span>
                </td>
                <td className="stat-change-cell">
                  {stat.change ? (
                    <div className={`stat-change ${stat.change.text === 'New' ? 'new' : (stat.change.positive ? 'positive' : 'negative')}`}>
                      {stat.change.text === 'New' ? '✨' : (stat.change.positive ? <MdTrendingUp /> : <MdTrendingDown />)} {stat.change.text}
                    </div>
                  ) : (
                    <span className="no-change">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2><MdFlashOn /> Quick Actions</h2>
        <div className="actions-grid">
          <a href="/digital-cards/new" className="action-button">
            <div className="action-icon create">
              <MdAdd />
            </div>
            <div className="action-title">Create Card</div>
            <div className="action-subtitle">Add new digital ID</div>
          </a>
          
          <a href="/digital-cards" className="action-button">
            <div className="action-icon manage">
              <MdBadge />
            </div>
            <div className="action-title">Manage Cards</div>
            <div className="action-subtitle">View & edit cards</div>
          </a>
          
          {(currentUser.role === 'super_admin' || currentUser.role === 'admin') && (
            <a href="/users" className="action-button">
              <div className="action-icon manage">
                <MdPeople />
              </div>
              <div className="action-title">Manage Users</div>
              <div className="action-subtitle">User permissions</div>
            </a>
          )}
          
          <a href="/settings" className="action-button">
            <div className="action-icon settings">
              <MdSettings />
            </div>
            <div className="action-title">Settings</div>
            <div className="action-subtitle">App configuration</div>
          </a>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <h2>Recent Activity</h2>
        {recentActivity.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><MdAccessTime /></div>
            <h3>No recent activity</h3>
            <p>Start by creating your first digital card or managing users.</p>
          </div>
        ) : (
          <div className="activity-list">
            {recentActivity.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className="activity-avatar">
                  {activity.user.charAt(0).toUpperCase()}
                </div>
                <div className="activity-content">
                  <div className="activity-text">
                    <strong>{activity.user}</strong> {activity.action}
                  </div>
                  <div className="activity-time">
                    {getTimeAgo(activity.time)}
                  </div>
                </div>
                <div className={`activity-badge ${activity.badge}`}>
                  {activity.type === 'card_created' ? 'Card' : 'User'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Digital Cards */}
      <div className="recent-activity">
        <h2>Recent Digital Cards</h2>
        {digitalCards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><MdBadge /></div>
            <h3>No digital cards yet</h3>
            <p>Create your first digital card to get started.</p>
            <a href="/digital-cards/new" className="create-card-link">
              Create Digital Card
            </a>
          </div>
        ) : (
          <div className="cards-list">
            {digitalCards.slice(0, 5).map(card => (
              <div key={card.id} className="card-item">
                <div className="card-info">
                  {card.profile_image ? (
                    <img 
                      src={card.profile_image} 
                      alt="Profile" 
                      className="card-avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <FaUserCircle 
                    className="card-avatar card-avatar-icon"
                    style={{ display: card.profile_image ? 'none' : 'block' }}
                  />
                  <div>
                    <h4>{card.name}</h4>
                    <p>{card.position} • {card.company_name}</p>
                  </div>
                </div>
                <div className="card-status">
                  <span className={`status-badge ${card.status || 'draft'}`}>
                    {(card.status || 'draft') === 'published' ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
